import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Modal,
  InputAdornment,
  IconButton,
  Fade,
  Backdrop,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Visibility, VisibilityOff, Close } from '@mui/icons-material';
import imgUrl from '../../assets/images/imgUrl';
import { apiServices } from '../../services/apiServices';
import Swal from 'sweetalert2';

const GiftsLogin = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [productUrl, setProductUrl] = useState(null);

  // Get stored product URL on component mount
  useEffect(() => {
    const storedProductUrl = localStorage.getItem('giftProductUrl');
    if (storedProductUrl) {
      setProductUrl(storedProductUrl);
    }
  }, []);

  // Modal state
  const [openReferralModal, setOpenReferralModal] = useState(false);
  const [openEmailModal, setOpenEmailModal] = useState(false);

  // Referral login state
  const [referralCode, setReferralCode] = useState('');

  // Email/Peacekeeper ID login state
  const [peacekeeperId, setPeacekeeperId] = useState('');
  const [peacekeeperPassword, setPeacekeeperPassword] = useState('');
  const [showPeacekeeperPassword, setShowPeacekeeperPassword] = useState(false);

  const handleReferralLogin = async (e) => {
    e.preventDefault();
    
    if (!referralCode.trim()) {
      Swal.fire({
        title: 'Error',
        text: 'Please enter your referral code',
        icon: 'error',
        confirmButtonText: 'OK',
        customClass: {
          popup: 'minimal-popup',
          confirmButton: 'minimal-button',
        },
        backdrop: false,
        allowOutsideClick: true,
        buttonsStyling: false,
      });
      return;
    }

    try {
      setIsLoading(true);
      
      // Call the check-referral API endpoint
      const response = await apiServices.getAPI(`user/check-referral/${referralCode.trim()}`);

      if (response.data && response.data.success) {
        const referralData = response.data.data;
        console.log('Referral data retrieved:', referralData);
        
        setOpenReferralModal(false);
        
        Swal.fire({
          title: 'Success',
          text: response.data.message || 'Referral verified successfully!',
          icon: 'success',
          showConfirmButton: false,
          timer: 1500,
          customClass: {
            popup: 'minimal-popup',
            confirmButton: 'minimal-button',
          },
          backdrop: false,
        });

        // Redirect to stored product URL if available, otherwise use referral URL or fallback
        if (productUrl) {
          localStorage.removeItem('giftProductUrl'); // Clear stored URL
          let temp = referralData.shopify_Referral_ShoppingURL.split('?')[1]
          window.open(productUrl+'?'+temp);
        } else if (referralData.shopify_Referral_ShoppingURL) {
          window.open(referralData.shopify_Referral_ShoppingURL);
        } else {
          // Fallback to main shop if no referral URL
          window.open('https://shop.i-am-peacekeeper.com/');
        }
      } else {
        throw new Error(response.data?.message || 'Invalid referral code');
      }
    } catch (error) {
      console.error('Referral login error:', error);
      Swal.fire({
        title: 'Login Failed',
        text: error.response?.data?.message || error.message || 'Invalid referral code. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK',
        customClass: {
          popup: 'minimal-popup',
          confirmButton: 'minimal-button',
        },
        backdrop: false,
        allowOutsideClick: true,
        buttonsStyling: false,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePeacekeeperLogin = async (e) => {
    e.preventDefault();
    
    if (!peacekeeperId.trim() || !peacekeeperPassword.trim()) {
      Swal.fire({
        title: 'Error',
        text: 'Please enter both email and password',
        icon: 'error',
        confirmButtonText: 'OK',
        customClass: {
          popup: 'minimal-popup',
          confirmButton: 'minimal-button',
        },
        backdrop: false,
        allowOutsideClick: true,
        buttonsStyling: false,
      });
      return;
    }

    try {
      setIsLoading(true);
      
      // Use peacekeeperId as email directly
      const userEmail = peacekeeperId.trim();
      
      // Call Shopify login API directly
      const shopifyLoginResponse = await apiServices.postAPI('shopify-login', {
        email: userEmail,
        password: peacekeeperPassword
      });

      if (shopifyLoginResponse.data && shopifyLoginResponse.data.success) {
        setOpenEmailModal(false);
        Swal.fire({
          title: 'Success',
          text: 'Login successful!',
          icon: 'success',
          showConfirmButton: false,
          timer: 1500,
          customClass: {
            popup: 'minimal-popup',
            confirmButton: 'minimal-button',
          },
          backdrop: false,
        });

        console.log(shopifyLoginResponse.data.redirectUrl)
        
        // Redirect to stored product URL if available, otherwise use API redirectUrl or fallback
        if (productUrl) {
          localStorage.removeItem('giftProductUrl'); // Clear stored URL
          let temp = shopifyLoginResponse.data.redirectUrl.split('?')[1]
          window.open(productUrl+'?'+temp);
        } else if (shopifyLoginResponse.data.redirectUrl) {
          //window.location.href = shopifyLoginResponse.data.redirectUrl;
          window.open(shopifyLoginResponse.data.redirectUrl)
        } else {
          // Fallback to dashboard if no redirectUrl
          navigate('/dashboard');
        }
      } else {
        throw new Error(shopifyLoginResponse.data?.message || 'Login failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      Swal.fire({
        title: 'Login Failed',
        text: error.response?.data?.message || error.message || 'Invalid email or password. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK',
        customClass: {
          popup: 'minimal-popup',
          confirmButton: 'minimal-button',
        },
        backdrop: false,
        allowOutsideClick: true,
        buttonsStyling: false,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleExternalLink = () => {
    // Redirect to product URL if available, otherwise redirect to shop homepage
    if (productUrl) {
      window.open(productUrl, '_self');
    } else {
      window.open('https://shop.i-am-peacekeeper.com/', '_self');
    }
  };

  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: { xs: '90%', sm: '400px' },
    bgcolor: 'background.paper',
    borderRadius: 2,
    boxShadow: 24,
    p: 4,
    outline: 'none',
  };

  return (
    <>
      {/* Hero Background Section */}
      <Box
        className="programGiftSectionBg commonBannerBg"
        sx={{
          backgroundSize: "cover",
          backgroundPosition: "center",
          color: "white",
          px: 2,
          minHeight: '200px',
          position: 'relative'
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 80,
            left: 0,
            display: "flex",
            alignItems: "center",
          }}
        >
          <Box
            component="img"
            src={imgUrl.doveImg}
            alt="Peace Dove Logo"
            sx={{ maxWidth: 200, height: "auto", mr: 1, cursor: 'pointer' }}
            onClick={() => navigate('/')}
          />
        </Box>
      </Box>

      {/* Login Section */}
      <Box sx={{ py: 6, px: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 'calc(100vh - 200px)' }}>
        <Box sx={{ width: '100%', maxWidth: { xs: '100%', md: '500px' }, mx: 'auto' }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <img src={imgUrl.peaceIcon} className="w-100 peaceImg" style={{ maxWidth: '100px' }} alt="Peace Icon" />
            <Typography variant="h4" className="founder-title mainHeading green-title" sx={{ mt: 2, mb: 1 }}>
              I Am Peacekeeper Gifts
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Choose your login method
            </Typography>
          </Box>

          {/* Three Vertical Buttons */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 4 }}>
            <Button
              variant="contained"
              onClick={() => setOpenReferralModal(true)}
              sx={{
                background: 'linear-gradient(to right, #214F7D, #3A8139)',
                color: 'white',
                borderRadius: '50px',
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 500,
                textTransform: 'none',
                '&:hover': {
                  background: 'linear-gradient(to right, #1a3f63, #2d6329)',
                  transform: 'translateY(-2px)',
                  boxShadow: 4,
                },
                transition: 'all 0.3s',
              }}
            >
              Referral Login
            </Button>

            <Button
              variant="contained"
              onClick={() => setOpenEmailModal(true)}
              sx={{
                background: 'linear-gradient(to right, #214F7D, #3A8139)',
                color: 'white',
                borderRadius: '50px',
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 500,
                textTransform: 'none',
                '&:hover': {
                  background: 'linear-gradient(to right, #1a3f63, #2d6329)',
                  transform: 'translateY(-2px)',
                  boxShadow: 4,
                },
                transition: 'all 0.3s',
              }}
            >
              PeaceKeeper Login
            </Button>

            <Button
              variant="contained"
              onClick={handleExternalLink}
              sx={{
                background: 'linear-gradient(to right, #214F7D, #3A8139)',
                color: 'white',
                borderRadius: '50px',
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 500,
                textTransform: 'none',
                '&:hover': {
                  background: 'linear-gradient(to right, #1a3f63, #2d6329)',
                  transform: 'translateY(-2px)',
                  boxShadow: 4,
                },
                transition: 'all 0.3s',
              }}
            >
              Continue as Guest
            </Button>
          </Box>

          {/* Back to Gifts Link */}
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Button
              variant="text"
              onClick={() => navigate('/Gifts')}
              sx={{
                color: '#214F7D',
                textTransform: 'none',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              ← Back to IAP Gifts
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Referral Login Modal */}
      <Modal
        open={openReferralModal}
        onClose={() => setOpenReferralModal(false)}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={openReferralModal}>
          <Box sx={modalStyle}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h5" className="green-title" sx={{ fontWeight: 600 }}>
                Login with Referral
              </Typography>
              <IconButton
                onClick={() => setOpenReferralModal(false)}
                sx={{ color: 'text.secondary' }}
              >
                <Close />
              </IconButton>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Enter your referral code to login
            </Typography>

            <Box component="form" onSubmit={handleReferralLogin}>
              <TextField
                fullWidth
                label="Referral Code"
                type="text"
                value={referralCode}
                onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                margin="normal"
                required
                placeholder="Enter your referral code"
                sx={{ mb: 3 }}
                inputProps={{
                  style: { textTransform: 'uppercase' }
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={isLoading}
                sx={{
                  background: 'linear-gradient(to right, #214F7D, #3A8139)',
                  color: 'white',
                  borderRadius: '50px',
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 500,
                  '&:hover': {
                    background: 'linear-gradient(to right, #1a3f63, #2d6329)',
                  }
                }}
              >
                {isLoading ? 'Logging in...' : 'Login'}
              </Button>
            </Box>
          </Box>
        </Fade>
      </Modal>

      {/* Email/Peacekeeper ID Login Modal */}
      <Modal
        open={openEmailModal}
        onClose={() => setOpenEmailModal(false)}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={openEmailModal}>
          <Box sx={modalStyle}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h5" className="green-title" sx={{ fontWeight: 600 }}>
                Login with Email
              </Typography>
              <IconButton
                onClick={() => setOpenEmailModal(false)}
                sx={{ color: 'text.secondary' }}
              >
                <Close />
              </IconButton>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Use your email and password
            </Typography>

            <Box component="form" onSubmit={handlePeacekeeperLogin}>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={peacekeeperId}
                onChange={(e) => setPeacekeeperId(e.target.value)}
                margin="normal"
                required
                placeholder="Enter your email"
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Password"
                type={showPeacekeeperPassword ? 'text' : 'password'}
                value={peacekeeperPassword}
                onChange={(e) => setPeacekeeperPassword(e.target.value)}
                margin="normal"
                required
                placeholder="Enter your password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPeacekeeperPassword(!showPeacekeeperPassword)}
                        edge="end"
                      >
                        {showPeacekeeperPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 3 }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={isLoading}
                sx={{
                  background: 'linear-gradient(to right, #214F7D, #3A8139)',
                  color: 'white',
                  borderRadius: '50px',
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 500,
                  '&:hover': {
                    background: 'linear-gradient(to right, #1a3f63, #2d6329)',
                  }
                }}
              >
                {isLoading ? 'Logging in...' : 'Login'}
              </Button>
            </Box>
          </Box>
        </Fade>
      </Modal>
    </>
  );
};

export default GiftsLogin;
